import type {
  PaidArchiveDateField,
  PaidArchiveStatus,
} from "../../api/queries/paymentsQueries";

export type PaidInvoicesFiltersProps = {
  customerId?: number;
  customerOptions: { id: number; name: string }[];
  statuses: PaidArchiveStatus[];
  dateField: PaidArchiveDateField;
  dateFrom?: string;
  dateTo?: string;
  onCustomerIdChange: (customerId: number | undefined) => void;
  onStatusesChange: (statuses: PaidArchiveStatus[]) => void;
  onDateFieldChange: (dateField: PaidArchiveDateField) => void;
  onDateFromChange: (dateFrom: string) => void;
  onDateToChange: (dateTo: string) => void;
  onClear: () => void;
};

const DATE_FIELD_OPTIONS: { value: PaidArchiveDateField; label: string }[] = [
  { value: "paid_or_closed", label: "Paid / closed date" },
  { value: "invoice", label: "Invoice date" },
  { value: "due", label: "Due date" },
];

export function PaidInvoicesFilters({
  customerId,
  customerOptions,
  statuses,
  dateField,
  dateFrom,
  dateTo,
  onCustomerIdChange,
  onStatusesChange,
  onDateFieldChange,
  onDateFromChange,
  onDateToChange,
  onClear,
}: PaidInvoicesFiltersProps) {
  const toggleStatus = (status: PaidArchiveStatus) => {
    if (statuses.includes(status)) {
      if (statuses.length === 1) return;
      onStatusesChange(statuses.filter((s) => s !== status));
      return;
    }
    onStatusesChange([...statuses, status]);
  };

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-gray-700">Customer</span>
          <select
            value={customerId ?? ""}
            onChange={(e) =>
              onCustomerIdChange(
                e.target.value ? Number(e.target.value) : undefined,
              )
            }
            className="rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-500"
          >
            <option value="">All customers</option>
            {customerOptions.map((customer) => (
              <option key={customer.id} value={customer.id}>
                {customer.name}
              </option>
            ))}
          </select>
        </label>

        <fieldset className="flex flex-col gap-1 text-sm">
          <legend className="font-medium text-gray-700">Status</legend>
          <div className="flex flex-wrap gap-3 pt-2">
            {(["Paid", "Closed"] as const).map((status) => (
              <label
                key={status}
                className="inline-flex items-center gap-2 text-gray-700"
              >
                <input
                  type="checkbox"
                  checked={statuses.includes(status)}
                  onChange={() => toggleStatus(status)}
                  className="rounded border-gray-300"
                />
                {status}
              </label>
            ))}
          </div>
        </fieldset>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-gray-700">Date field</span>
          <select
            value={dateField}
            onChange={(e) =>
              onDateFieldChange(e.target.value as PaidArchiveDateField)
            }
            className="rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-500"
          >
            {DATE_FIELD_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-gray-700">From</span>
          <input
            type="date"
            value={dateFrom ?? ""}
            onChange={(e) => onDateFromChange(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-500"
          />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-gray-700">To</span>
          <input
            type="date"
            value={dateTo ?? ""}
            onChange={(e) => onDateToChange(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-500"
          />
        </label>
      </div>

      <div>
        <button
          type="button"
          onClick={onClear}
          className="rounded-lg border border-gray-300 bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-200"
        >
          Clear filters
        </button>
      </div>
    </div>
  );
}
